import { computed } from 'vue';

/**
 * 基于 localStorage 中的 permissionList 进行按钮级权限校验。
 *
 * 与老的 usePermission（基于 userInfo.permCodeList）并行存在，
 * 供 /ht 业务页面在模板（配合 v-permission 指令）或 JS 逻辑中判断权限码。
 *
 * permissionList 在登录时写入、会话内不变，因此采用模块级缓存：
 * 以 localStorage 原始字符串为版本号，变化时重建 Set，避免重复解析。
 */

const PERMISSION_LIST_KEY = 'permissionList';

/** 超级管理员通配符，命中即视为拥有全部权限 */
const WILDCARD_ALL = '*';

// 缓存：解析后的权限码集合
let cachedSet: Set<string> = new Set<string>();
// 缓存对应的 localStorage 原始值，用于判断是否需要重建集合
let cachedRaw: string | null = null;

/**
 * 从 localStorage 读取并解析 permissionList 为 Set。
 * 当原始值未变化时直接复用缓存，避免每次校验都 JSON.parse。
 *
 * @returns 权限码集合（解析失败或为空时返回空集合）
 */
const getPermissionSet = (): Set<string> => {
  const raw = localStorage.getItem(PERMISSION_LIST_KEY);
  if (raw === cachedRaw) {
    return cachedSet;
  }

  cachedRaw = raw;
  const set = new Set<string>();
  if (raw) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        for (const item of parsed) {
          if (typeof item === 'string') set.add(item);
        }
      }
    } catch {
      // 解析失败视为无权限，返回空集合
      set.clear();
    }
  }
  cachedSet = set;
  return set;
};

/**
 * 重置 permissionList 缓存。
 *
 * 登出或手动清空 localStorage 后调用，确保下次校验重新读取最新值。
 */
export const resetPermissionListCache = (): void => {
  cachedSet = new Set<string>();
  cachedRaw = null;
};

export const usePermissionList = () => {
  /**
   * 判断当前用户是否拥有指定权限码。
   * 若 permissionList 包含通配符 "*"，视为拥有全部权限。
   *
   * @param permCode - 权限码，如 "analog:add"
   * @returns 拥有该权限返回 true，否则 false
   */
  const hasPermission = (permCode: string): boolean => {
    if (!permCode) return false;
    const set = getPermissionSet();
    if (set.has(WILDCARD_ALL)) return true;
    return set.has(permCode);
  };

  /**
   * 判断是否拥有给定权限码列表中的任意一个（OR 关系）。
   *
   * @param permCodes - 权限码数组
   * @returns 满足其中任意一个即返回 true；空数组返回 false
   */
  const hasAnyPermission = (permCodes: string[]): boolean => {
    if (!permCodes || permCodes.length === 0) return false;
    const set = getPermissionSet();
    if (set.has(WILDCARD_ALL)) return true;
    return permCodes.some(code => set.has(code));
  };

  /**
   * 判断是否同时拥有给定权限码列表中的全部权限（AND 关系）。
   *
   * @param permCodes - 权限码数组
   * @returns 全部满足才返回 true；空数组返回 false
   */
  const hasAllPermissions = (permCodes: string[]): boolean => {
    if (!permCodes || permCodes.length === 0) return false;
    const set = getPermissionSet();
    if (set.has(WILDCARD_ALL)) return true;
    return permCodes.every(code => set.has(code));
  };

  /** 当前 permissionList 的只读副本 */
  const permissionList = computed<string[]>(() => Array.from(getPermissionSet()));

  return {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    permissionList,
  };
};
